import React, { useEffect, useRef, useState } from "react";
import styles from "./TicketUpload.module.scss";
import ConditionalRender from "../../reusable-components/ConditionalRender";
import CustomTableWithPagination from "../../reusable-components/CustomTable";
import {
  fieldNames,
  FilterButtonStyles,
  searchValidationSchema,
  TABLE_PAGESIZE_OPTIONS,
  getTicketColumns,
  ticketViewConstants,
  FILTER_FIELD_NAMES_TO_COUNT,
} from "./constants";
import Form from "../Form";
import { ButtonShape, ButtonSize } from "../../enums/ButtonAttributesEnums";
import InputField from "../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../enums/InputFieldAttributesEnums";
import TNButton from "../../reusable-components/TNButton";
import SearchIcon from "../../assets/images/search-button.svg";
import FilterIcon from "../../assets/images/filterIcon.svg";
import TicketViewFilter from "./TicketFilterDrawer";
import TicketUploadServices from "../../services/ticketUpload.service";
import TicketUploadDrawer from "./TicketUploadDrawer";
import { TicketUploadFilterProps } from "../../Types";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { appRoutes } from "../../constants/appRoutes";
import { TimeTypes } from "../../enums/dateTimeFormat";
import { regexPatterns } from "../../utils/regexUtil";
import { message } from "antd";
import RolesServices from "../../services/roles.services";
import { UserRoleActions } from "../../enums/UserRolesEnum";
import { storageKeys } from "../../constants/storageKeys";
import { cleanParams } from "../../Utilities/queryParamUtils";
import JobDatabaseService from "../../services/jobDatabase.service";
import TNLoader from "../../reusable-components/TNLoader";
import { ApiResponse } from "../../enums/ApiResponse";
import { countFiltersApplied } from "../../utils/countAppliedFilters";

const TicketUploadView = () => {
  const isMountRef = useRef<boolean>(true);
  const navigate = useHistory();
  const { GetPaginationDefaultMeta } = JobDatabaseService();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    Number(sessionStorage.getItem(storageKeys.TICKET_UPLOAD_PAGESIZE) ?? 10),
  );
  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [filters, setFilters] = useState({
    search: "",
    companyIds: [],
    truckIds: [],
    orderIds: [],
    jobNumbers: [],
    jobNames: [],
    fromDate: undefined,
    toDate: undefined,
  });
  const [searchValue, setSearchValue] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [clearAllButton, setClearAllButton] = useState<boolean>(false);

  const userDetails = localStorage.getItem(storageKeys.USER_DETAILS_KEY);
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.[storageKeys.USER_ROLE_KEY];
  const userRoleProfileInfo = userDetailsJson?.[storageKeys.USER_PROFILE_KEY];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: defaultData, isFetching: gettingDefaults } =
    GetPaginationDefaultMeta();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const allowReviewAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[0]?.selectedOptions?.includes(
        UserRoleActions.REVIEW,
      )
    : true;

  const { FetchUploadedTickets, UpdateTicketLockStatus } =
    TicketUploadServices();
  const {
    data,
    isLoading,
    isFetching,
    isRefetching,
    refetch,
    isSuccess: listFetchedSuccess,
  } = FetchUploadedTickets(
    {
      search: filters?.search,
      page: currentPage,
      perPage: pageSize,
      companyId: filters?.companyIds,
      truckId: filters?.truckIds,
      orderId: filters?.orderIds,
      jobNumber: filters?.jobNumbers,
      jobName: filters?.jobNames,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
    },
    pageSizeChanged,
    !!sessionStorage.getItem(storageKeys.TICKET_UPLOAD_PAGESIZE),
  );
  const { mutateAsync: updateTicketLockStatus } = UpdateTicketLockStatus();
  const ticketColumns = getTicketColumns({
    updateTicketLockStatus: updateTicketLockStatus,
  });

  const filterCount = countFiltersApplied(filters, FILTER_FIELD_NAMES_TO_COUNT);

  const handleSearch = (val?: string) => {
    const searchVal = val || "";
    if (!!searchVal && !regexPatterns.searchRegex.test(searchVal)) {
      message.error(ticketViewConstants.SEARCH_ERROR_TXT);
    } else {
      setSearchValue(searchVal);
      setSearchInputValue(searchVal);
    }
  };

  const handleSearchClick = (currentSearchValue: string) => {
    handleSearch(currentSearchValue);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (!value) {
      handleSearch("");
    }
  };

  const handleGetFilteredResults = ({
    customerNames,
    truckNumbers,
    orderIds,
    dateRange,
    fromDate,
    toDate,
    jobNumbers,
    jobNames,
  }: TicketUploadFilterProps) => {
    setCurrentPage(1);
    setFilters({
      ...filters,
      companyIds: customerNames,
      truckIds: truckNumbers,
      orderIds: orderIds,
      jobNumbers: jobNumbers,
      jobNames: jobNames,
      ...(!!fromDate
        ? {
            fromDate: moment(fromDate).startOf(TimeTypes.DAY).valueOf(),
            toDate: moment(toDate).endOf(TimeTypes.DAY).valueOf(),
          }
        : {
            fromDate: undefined,
            toDate: undefined,
          }),
    });
    handleCloseFilterDrawer();
  };

  const handleClearAllFilter = () => {
    setFilters({
      ...filters,
      companyIds: [],
      truckIds: [],
      orderIds: [],
      jobNumbers: [],
      jobNames: [],
      fromDate: undefined,
      toDate: undefined,
    });
    handleEnableClearAllButton();
  };

  const handleClearFilter = () => {
    handleClearAllFilter();
    handleCloseFilterDrawer();
  };

  const handleEnableClearAllButton = () => {
    setClearAllButton(true);
  };

  const handleDisableClearAllButton = () => {
    setClearAllButton(false);
  };

  const handleOpenFilterDrawer = () => {
    setIsFilterOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setIsFilterOpen(false);
  };

  const handleOpenUploadDrawer = () => {
    setIsUploadOpen(true);
  };

  const handleCloseUploadDrawer = () => {
    setIsUploadOpen(false);
  };

  const handleNavigateViewTicket = (row) => {
    if (row?.locked) {
      return;
    }
    if (allowReviewAccess) {
      navigate.push(`${appRoutes.VIEW_TICKET_UPLOAD_REVIEW}${row?.id}`);
    } else message.error(ticketViewConstants.REVIEW_ACCESS_ERROR);
  };

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };

  const handlePagination = (page: number) => handleCurrentPage(page);

  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    setPageSizeChanged(true);
    try {
      sessionStorage.setItem(storageKeys.TICKET_UPLOAD_PAGESIZE, size);
    } catch (err) {
      console.error("Err", err);
    }
  };

  useEffect(() => {
    setFilters({
      ...filters,
      search: searchInputValue,
    });
    setCurrentPage(1);
  }, [searchInputValue]);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    refetch();
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    const htmlEl = document.documentElement;
    if (isUploadOpen) {
      htmlEl.classList.add("disable-scroll");
    } else {
      htmlEl.classList.remove("disable-scroll");
    }
    return () => {
      htmlEl.classList.remove("disable-scroll");
    };
  }, [isUploadOpen]);

  useEffect(() => {
    if (
      !!defaultData?.message &&
      defaultData?.message === ApiResponse.SUCCESS
    ) {
      setPageSize(defaultData?.data?.uploadDefaultPageSize);
      sessionStorage.setItem(
        storageKeys.TICKET_UPLOAD_PAGESIZE,
        defaultData?.data?.uploadDefaultPageSize,
      );
    }
  }, [defaultData]);

  useEffect(() => {
    if (listFetchedSuccess && pageSizeChanged) {
      setPageSizeChanged(false);
    }
  }, [listFetchedSuccess, pageSizeChanged]);

  return (
    <>
      <TNLoader
        isLoading={isLoading || isFetching || isRefetching || gettingDefaults}
      />
      <div className={styles.headerSection}>
        <Form>
          {() => (
            <TicketViewFilter
              isOpen={isFilterOpen}
              handleClose={handleCloseFilterDrawer}
              handleApplyFilter={handleGetFilteredResults}
              handleClearFilter={handleClearFilter}
              filterValues={filters}
              clearAllButton={clearAllButton}
              handleDisableClearAllButton={handleDisableClearAllButton}
            />
          )}
        </Form>

        <TicketUploadDrawer
          isOpen={isUploadOpen}
          handleClose={handleCloseUploadDrawer}
          refetchTickets={refetch}
          allowReview={allowReviewAccess}
        />
        <div className={styles.leftContainer}>
          <Form validationSchema={searchValidationSchema}>
            {({ watch, control: { handleSubmit } }) => {
              const currentSearchValue = watch(fieldNames.SEARCH);
              const addOnAfter = (
                <TNButton
                  shape={ButtonShape.DEFAULT}
                  size={ButtonSize.LARGE}
                  block
                  text={
                    <img
                      src={SearchIcon}
                      className={styles.searchFieldIcon}
                      alt={ticketViewConstants.SEARCH_ICON_ALT}
                    />
                  }
                  onClick={handleSubmit((formData) =>
                    handleSearchClick(formData[fieldNames.SEARCH]),
                  )}
                />
              );

              return (
                <InputField
                  type={InputfieldTypes.TEXT}
                  placeholder={ticketViewConstants.SEARCH_PLACEHOLDER}
                  addonAfter={addOnAfter}
                  name={fieldNames.SEARCH}
                  value={searchInputValue}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    setSearchInputValue(e.target.value);
                    if (!e.target.value) {
                      handleSubmit(() => handleSearch(""))();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit(() => handleSearch(currentSearchValue))();
                    }
                  }}
                  maxTextLength={50}
                />
              );
            }}
          </Form>
          <TNButton
            filterButton
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            badgeCount={filterCount}
            text="Filter"
            afterTitle={true}
            icon={<img src={FilterIcon} />}
            onClick={handleOpenFilterDrawer}
          />
          {filterCount > 0 && (
            <TNButton
              clearFilterButton
              shape={ButtonShape.ROUND}
              size={ButtonSize.MEDIUM}
              text="Clear All"
              onClick={handleClearAllFilter}
            />
          )}
        </div>
        <div className={styles.rightContainer}>
          <TNButton
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            text={ticketViewConstants.UPLOAD_TKT_BUTTON_TEXT}
            onClick={handleOpenUploadDrawer}
          />
        </div>
      </div>
      <div className={styles.tableSection}>
        <ConditionalRender
          visible={data?.data?.length}
          fallback={
            <div className={styles.emptyContainer}>
              <p>
                {Object?.keys(cleanParams(filters))?.length > 0
                  ? ticketViewConstants.EMPTY_SEARCH_MSG
                  : ticketViewConstants.EMPTY_TABLE_MSG}
              </p>
            </div>
          }
        >
          <CustomTableWithPagination
            bordered={false}
            isClickableTable
            pageSizeOptions={TABLE_PAGESIZE_OPTIONS}
            pageSize={pageSize}
            currentPage={currentPage}
            totalData={data?.meta?.totalRecords}
            columns={ticketColumns}
            rowClassName={(record) =>
              `${styles.ticketTableRows} ${
                record?.locked ? styles.lockedRow : ""
              }`
            }
            dataSource={data?.data ?? []}
            handleChange={handlePagination}
            handleSizeChangerChange={handlePaginationPageSize}
            handleSelectedRow={handleNavigateViewTicket}
          />
        </ConditionalRender>
      </div>
    </>
  );
};

export default TicketUploadView;
